/**
 * UnmaskProvider — owns the customer dashboard "unmasked" state.
 *
 * Responsibilities:
 *   - Hold { isDashboardUnmasked, unmaskExpiresAt } for the whole protected app.
 *   - On a successful OTP verify, swap ONLY the access token for the short-lived
 *     unmask token and refetch the masked surfaces so they come back revealed.
 *   - Run an auto-revert timer: when the unmask window elapses, drop back to
 *     masked mode and refetch.
 *
 * Token-split contract (see packages/auth/token-store.ts): we call
 * tokenStore.setTokens({ accessToken }) and leave refresh + firestore tokens
 * untouched — so a normal /auth/refresh keeps working and can never resurrect
 * the unmasked state. That is exactly why "revert" needs no custom refresh
 * logic: the access token in storage is the now-expired unmask token, so the
 * next request 401s and the api-client transparently refreshes to a normal
 * (masked) token and retries. We only have to refetch.
 *
 * Persistence: the expiry is mirrored into sessionStorage (tab-scoped, like the
 * access token) so a tab refresh inside the window resumes the timer/badge. It
 * is keyed to the customer's user id and only restored once auth settles to the
 * SAME user — otherwise a logout → login in the same tab (sessionStorage
 * survives that) would wrongly resurrect the previous user's unmasked state.
 */
import * as React from 'react';
import { useAuth } from '@finmin/auth';
import { tokenStore, UNMASK_STORAGE_KEY } from '@/lib/api';
import { queryClient } from '@/api/query-client';
import { profileKeys } from '@/api/profile';
import { serviceRequestKeys } from '@/api/service-requests';

const isBrowser = typeof window !== 'undefined';

interface PersistedUnmask {
  /** Customer user id this unmask window belongs to. */
  uid: string;
  /** Epoch-ms when the window ends. */
  expiresAt: number;
}

export interface UnmaskContextValue {
  /** True while a valid short-lived unmask token is in effect. */
  isDashboardUnmasked: boolean;
  /** Epoch-ms when the unmask window ends, or null while masked. */
  unmaskExpiresAt: number | null;
  /** Apply the token returned by /unmask/verify and refetch dashboard data. */
  applyUnmaskToken: (accessToken: string, expiresInSec: number) => void;
  /** Drop back to masked mode immediately and refetch. */
  revert: () => void;
}

const UnmaskContext = React.createContext<UnmaskContextValue | null>(null);

function readPersisted(): PersistedUnmask | null {
  if (!isBrowser) return null;
  try {
    const raw = sessionStorage.getItem(UNMASK_STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as PersistedUnmask;
    if (typeof parsed?.uid !== 'string' || !Number.isFinite(parsed?.expiresAt)) return null;
    return parsed;
  } catch {
    return null;
  }
}

function clearPersisted() {
  if (isBrowser) sessionStorage.removeItem(UNMASK_STORAGE_KEY);
}

export function UnmaskProvider({ children }: { children: React.ReactNode }) {
  const { user, status } = useAuth();
  const userId = user?.id ?? null;

  const [unmaskExpiresAt, setUnmaskExpiresAt] = React.useState<number | null>(null);

  /** Refetch only the surfaces the backend masks. Rewards/notifications etc. are untouched. */
  const refetchMaskedSurfaces = React.useCallback(() => {
    void queryClient.invalidateQueries({ queryKey: profileKeys.all });
    void queryClient.invalidateQueries({ queryKey: serviceRequestKeys.all });
  }, []);

  const applyUnmaskToken = React.useCallback(
    (accessToken: string, expiresInSec: number) => {
      // Replace ONLY the access token — refresh + firestore tokens stay put.
      tokenStore.setTokens({ accessToken });
      const expiresAt = Date.now() + expiresInSec * 1000;
      if (isBrowser && userId) {
        sessionStorage.setItem(UNMASK_STORAGE_KEY, JSON.stringify({ uid: userId, expiresAt }));
      }
      setUnmaskExpiresAt(expiresAt);
      refetchMaskedSurfaces();
    },
    [refetchMaskedSurfaces, userId],
  );

  const revert = React.useCallback(() => {
    clearPersisted();
    setUnmaskExpiresAt(null);
    // Access token is the now-expired unmask token: the next request 401s and
    // the api-client refreshes to a normal masked token, then retries.
    refetchMaskedSurfaces();
  }, [refetchMaskedSurfaces]);

  // Restore-or-clear when auth settles / the user changes. Guards against the
  // unmasked state leaking across logout → login in the same tab.
  React.useEffect(() => {
    if (status === 'loading') return; // wait until we know who's logged in
    const persisted = readPersisted();
    if (
      status === 'authenticated' &&
      persisted &&
      persisted.uid === userId &&
      persisted.expiresAt > Date.now()
    ) {
      setUnmaskExpiresAt(persisted.expiresAt);
    } else {
      if (persisted) clearPersisted();
      setUnmaskExpiresAt(null);
    }
  }, [status, userId]);

  // Auto-revert timer. Re-runs whenever the expiry changes (set on verify,
  // restored above, cleared on revert).
  React.useEffect(() => {
    if (unmaskExpiresAt === null) return;
    const ms = Math.max(0, unmaskExpiresAt - Date.now());
    const timer = setTimeout(() => revert(), ms);
    return () => clearTimeout(timer);
  }, [unmaskExpiresAt, revert]);

  const value = React.useMemo<UnmaskContextValue>(
    () => ({
      isDashboardUnmasked: unmaskExpiresAt !== null,
      unmaskExpiresAt,
      applyUnmaskToken,
      revert,
    }),
    [unmaskExpiresAt, applyUnmaskToken, revert],
  );

  return <UnmaskContext.Provider value={value}>{children}</UnmaskContext.Provider>;
}

export function useUnmask(): UnmaskContextValue {
  const ctx = React.useContext(UnmaskContext);
  if (!ctx) throw new Error('useUnmask must be used within <UnmaskProvider>');
  return ctx;
}
